package com.karawek.core.util

/**
 * Utilities for date formatting and conversion.
 *
 * Display format: mm/dd/yyyy (e.g., "01/15/2024")
 */
object DateFormatter {

    /**
     * Formats a date string according to mm/dd/yyyy format as user types.
     * Used for real-time formatting in text fields.
     *
     * @param digitsOnly The raw digits (without formatting)
     * @return The formatted date string
     */
    fun formatAsTyping(digitsOnly: String): String {
        if (digitsOnly.isEmpty()) return ""

        val result = StringBuilder()
        val maxDigits = 8 // mmddyyyy

        // Limit to 8 digits
        val limitedDigits = digitsOnly.take(maxDigits)

        for (i in limitedDigits.indices) {
            result.append(limitedDigits[i])
            // Add slash after month (2 digits) and day (4 digits total)
            if (i == 1 || i == 3) {
                if (i < limitedDigits.length - 1) {
                    result.append('/')
                }
            }
        }

        return result.toString()
    }

    /**
     * Validates if a date string is in valid mm/dd/yyyy format.
     *
     * @param date The date string to validate
     * @return true if the date has exactly 8 digits and valid ranges
     */
    fun isValid(date: String): Boolean {
        val digitsOnly = date.filter { it.isDigit() }
        if (digitsOnly.length != 8) return false

        val month = digitsOnly.substring(0, 2).toIntOrNull() ?: return false
        val day = digitsOnly.substring(2, 4).toIntOrNull() ?: return false
        val year = digitsOnly.substring(4, 8).toIntOrNull() ?: return false

        // Basic validation
        if (month < 1 || month > 12) return false
        if (day < 1 || day > 31) return false
        if (year < 1900 || year > 2100) return false

        return true
    }

    /**
     * Extracts only digits from a formatted date string.
     *
     * @param formattedDate The formatted date string (e.g., "01/15/2024")
     * @return The digits only (e.g., "01152024")
     */
    fun toDigitsOnly(formattedDate: String): String {
        return formattedDate.filter { it.isDigit() }
    }
}
